import React, { useEffect, useState, useMemo } from 'react';
import { Box, Button, Flex, FlexProps, Spinner, Text, Input } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import arrowIcon from '../../Resources/Images/Icons/arrow_icon.svg';
import { BulletinTag } from '../../Utils/BulletinTag';
import { GenericTable } from '../Common/GenericTable';
import { TablePager } from '../Common/TablePager';

export interface VerificationRecordItem {
  id: number;
  verifyTime: string;
  result: string;
  certificateNo: string;
  studentName: string;
  certNo: string;
  module: string;
  loading?: boolean;
}

interface Props extends Pick<FlexProps, 'mt'> {
  records: VerificationRecordItem[];
}

const PAGE_SIZE_OPTIONS = [10, 20] as const;

type SortField = 'verifyTime' | 'result';
type SortDirection = 'asc' | 'desc';

const TABLE_CELL_TEXT_STYLE = {
  fontFamily: "'Open Sans', sans-serif",
  fontSize: '18px',
  fontWeight: 400,
  lineHeight: '150%',
  letterSpacing: '0.01em',
  color: '#00292B',
};

const normalizeDateTime = (dateStr: string): number => {
  if (!dateStr) return 0;

  const normalized = dateStr.replace(/\./g, '/').replace(/-/g, '/').trim();
  const parsed = new Date(normalized);

  if (!Number.isNaN(parsed.getTime())) {
    return parsed.getTime();
  }

  return 0;
};

const formatDisplayDateTime = (dateStr: string): string => {
  if (!dateStr) return '';

  const parsed = new Date(dateStr);
  if (!Number.isNaN(parsed.getTime())) {
    const year = parsed.getFullYear();
    const month = parsed.getMonth() + 1;
    const day = parsed.getDate();
    const hours = String(parsed.getHours()).padStart(2, '0');
    const minutes = String(parsed.getMinutes()).padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}`;
  }

  const normalized = dateStr.replace(/\./g, '/').replace(/-/g, '/').trim();
  return normalized;
};

export const VerificationRecordTable: React.FC<Props> = ({ records, mt }) => {
  const { t } = useTranslation();

  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortField, setSortField] = useState<SortField>('verifyTime');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');

  const handleSortToggle = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(field);
    setSortDirection(field === 'verifyTime' ? 'desc' : 'asc');
  };

  const filteredRecords = useMemo(() => {
    return records.filter((item) => {
      const itemTime = normalizeDateTime(item.verifyTime);

      const startMatched = !appliedStartDate || itemTime >= new Date(appliedStartDate).getTime();
      const endMatched = !appliedEndDate || itemTime <= new Date(`${appliedEndDate}T23:59:59`).getTime();

      const keywordLower = appliedKeyword.trim().toLowerCase();
      const keywordMatched =
        !keywordLower ||
        item.studentName.toLowerCase().includes(keywordLower) ||
        item.certificateNo.toLowerCase().includes(keywordLower) ||
        item.certNo.toLowerCase().includes(keywordLower) ||
        item.verifyModule.toLowerCase().includes(keywordLower);

      return startMatched && endMatched && keywordMatched;
    });
  }, [records, appliedStartDate, appliedEndDate, appliedKeyword]);

  const sortedRecords = useMemo(() => {
    const cloned = [...filteredRecords];

    cloned.sort((a, b) => {
      let compareValue = 0;

      if (sortField === 'verifyTime') {
        compareValue = normalizeDateTime(a.verifyTime) - normalizeDateTime(b.verifyTime);
      } else if (sortField === 'result') {
        compareValue = a.result.localeCompare(b.result, 'zh-Hant');
      }

      return sortDirection === 'asc' ? compareValue : -compareValue;
    });

    return cloned;
  }, [filteredRecords, sortField, sortDirection]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredRecords, sortField, sortDirection]);

  const totalItems = sortedRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = totalItems === 0 ? 0 : (safePage - 1) * pageSize;
  const paginated = sortedRecords.slice(startIndex, startIndex + pageSize);

  const columns = [
    {
      label: t('verificationRecord.verifyTime'),
      width: '180px',
      sortDirection: sortField === 'verifyTime' ? sortDirection : undefined,
      onSort: () => handleSortToggle('verifyTime'),
      sortArrowSrc: arrowIcon,
      sortArrowAlt: t('verificationRecord.sortArrowAlt'),
    },
    {
      label: t('verificationRecord.result'),
      width: '120px',
      sortDirection: sortField === 'result' ? sortDirection : undefined,
      onSort: () => handleSortToggle('result'),
      sortArrowSrc: arrowIcon,
      sortArrowAlt: t('verificationRecord.sortArrowAlt'),
    },
    { label: t('verificationRecord.certificateNo'), width: '180px' },
    { label: t('verificationRecord.studentName'), width: '160px' },
    { label: t('verificationRecord.certNo'), width: '180px' },
    { label: t('verificationRecord.verifyModule'), width: '220px' },
  ];

  const renderRow = (item: VerificationRecordItem) => (
    <Flex key={item.id} w="1040px" minH="60px" align="stretch">
      <Flex w="180px" minW="180px" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE}>{formatDisplayDateTime(item.verifyTime)}</Text>
      </Flex>

      <Flex w="120px" minW="120px" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE}>{item.result}</Text>
      </Flex>

      <Flex w="180px" minW="180px" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE} noOfLines={1} title={item.certificateNo}>
          {item.certificateNo}
        </Text>
      </Flex>

      <Flex w="160px" minW="160px" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE} noOfLines={1} title={item.studentName}>
          {item.studentName}
        </Text>
      </Flex>

      <Flex w="180px" minW="180px" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE} noOfLines={1} title={item.certNo}>
          {item.certNo}
        </Text>
      </Flex>

      <Flex w="220px" minW="220px" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE} noOfLines={1} title={item.verifyModule}>
          {item.verifyModule}
        </Text>
      </Flex>
    </Flex>
  );

  return (
    <Flex direction="column" w="1040px" mt={mt}>
      <Flex w="1040px" mb="24px" direction="column" align="flex-end" gap="12px">
        <Flex align="center" gap="8px" wrap="wrap" justify="flex-end">
          <Text fontSize="16px" fontWeight={600} color="#000000">
            {t('verificationRecord.dateRange')}
          </Text>

          <Text fontSize="16px" color="#000000">
            自
          </Text>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            w="160px"
            h="37px"
            bg="#FFFFFF"
            border="1px solid #A8B8C2"
            borderRadius="4px"
            fontSize="14px"
          />

          <Text fontSize="16px" color="#000000">
            至
          </Text>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            w="160px"
            h="37px"
            bg="#FFFFFF"
            border="1px solid #A8B8C2"
            borderRadius="4px"
            fontSize="14px"
          />
        </Flex>

        <Flex align="center" gap="8px" wrap="wrap" justify="flex-end">
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder={t('verificationRecord.searchPlaceholder')}
            w="320px"
            h="37px"
            bg="#FFFFFF"
            border="1px solid #A8B8C2"
            borderRadius="4px"
            fontSize="14px"
          />

          <Button
            variant="actionPrimary"
            h="37px"
            px="12px"
            onClick={() => {
              setAppliedKeyword(keyword);
              setAppliedStartDate(startDate);
              setAppliedEndDate(endDate);
              setCurrentPage(1);
            }}
          >
            {t('verificationRecord.search')}
          </Button>
          <Button
            variant="neutral"
            h="37px"
            px="12px"
            onClick={() => {
              setStartDate('');
              setEndDate('');
              setKeyword('');
              setAppliedStartDate('');
              setAppliedEndDate('');
              setAppliedKeyword('');
              setCurrentPage(1);
            }}
          >
            {t('verificationRecord.clear')}
          </Button>
        </Flex>
      </Flex>
      <GenericTable columns={columns} isEmpty={paginated.length === 0} emptyText={t('verificationRecord.empty')}>
        {paginated.map(renderRow)}
      </GenericTable>

      <TablePager
        mt="50px"
        totalItems={totalItems}
        pageSize={pageSize}
        currentPage={safePage}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setCurrentPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setCurrentPage(1);
        }}
        labels={{
          show: t('verificationRecord.show'),
          itemsUnit: t('verificationRecord.itemsUnit'),
          range: (s, e) => t('verificationRecord.range', { start: s, end: e }),
          prevPage: t('verificationRecord.prevPage'),
          nextPage: t('verificationRecord.nextPage'),
          pageSizeAriaLabel: t('verificationRecord.pageSizeAriaLabel'),
        }}
      />
    </Flex>
  );
};

export default VerificationRecordTable;
